import { z } from "zod";
import { AccountingLedgerBasis } from "../types/domains/accounting/ledger";
import { ChartOfAccountsTemplateStartingPoint } from "../constants/domains/accounting/chartOfAccountsTemplateStartingPoint";
import { ChartOfAccountsTemplateAccountsBulkSaveSchema } from "./chart-of-accounts-template-account.schema";
import { EntityProfileIndustry, EntityProfileType } from "../types/entity";
import { zQueryBoolean } from "./query-boolean.schema";
import { ChartOfAccountsTemplateListItemSchema } from "../types/chartOfAccountsTemplate";
import {
    ChartOfAccountsTemplateAccountGroupSchema,
    ChartOfAccountsTemplateAccountSchema,
} from "../types/domains/accounting/chartOfAccountsTemplateAccount";

export const ChartOfAccountsTemplatePathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const ChartOfAccountsTemplateIdPathParamsSchema = z.object({
    chartOfAccountsTemplateId: z.string().uuid(),
});

// Optional, de-duplicated set of accounting bases a template applies to. Basis
// is metadata only — it does not gate apply (PRO-9997) — so an empty set is
// allowed and simply means "no basis specified".
const basisSchema = z
    .array(z.nativeEnum(AccountingLedgerBasis))
    .max(2)
    .refine((b) => new Set(b).size === b.length, {
        message: "Basis must be unique",
    });

// Optional, de-duplicated set of industries a template applies to. Like basis,
// industry is metadata only — it does not gate apply (PRO-9997) — so an empty
// set is allowed and simply means "no industry specified".
const industriesSchema = z
    .array(z.nativeEnum(EntityProfileIndustry))
    .max(Object.values(EntityProfileIndustry).length)
    .refine((industries) => new Set(industries).size === industries.length, {
        message: "Industries must be unique",
    });

const chartOfAccountsTemplateHeaderShape = {
    name: z.string().min(1, { message: "Required" }).max(255),
    description: z.string().max(1000).nullable().optional(),
    basis: basisSchema.default([
        AccountingLedgerBasis.cash,
        AccountingLedgerBasis.accruals,
    ]),
    industries: industriesSchema.default([]),
};

// Header-only schema (no starting-point fields/refinements). Used for formik
// validation in the create modal, whose values only contain header fields.
export const ChartOfAccountsTemplateHeaderSchema = z.object({
    ...chartOfAccountsTemplateHeaderShape,
    // Required. The custom errorMap replaces Zod's default enum error
    // ("Expected 'sole_proprietorship' | ... , received null") with a clean
    // "Required" message for the empty (null) initial form state.
    entityLegalType: z.nativeEnum(EntityProfileType, {
        errorMap: () => ({ message: "Required" }),
    }),
});

export type ChartOfAccountsTemplateHeader = z.infer<
    typeof ChartOfAccountsTemplateHeaderSchema
>;

export const ChartOfAccountsTemplateCreateSchema =
    ChartOfAccountsTemplateHeaderSchema.extend({
        startingPoint: z
            .nativeEnum(ChartOfAccountsTemplateStartingPoint)
            .optional(),
        sourceTemplateId: z.string().uuid().optional(),
        sourceEntityId: z.number().int().positive().optional(),
        rows: ChartOfAccountsTemplateAccountsBulkSaveSchema.optional(),
    })
        .refine(
            (data) =>
                data.startingPoint !==
                    ChartOfAccountsTemplateStartingPoint.copyTemplate ||
                data.sourceTemplateId !== undefined,
            {
                message:
                    "sourceTemplateId is required when startingPoint is copyTemplate",
                path: ["sourceTemplateId"],
            },
        )
        .refine(
            (data) =>
                data.startingPoint !==
                    ChartOfAccountsTemplateStartingPoint.copyEntity ||
                data.sourceEntityId !== undefined,
            {
                message:
                    "sourceEntityId is required when startingPoint is copyEntity",
                path: ["sourceEntityId"],
            },
        )
        // Reject source fields that don't match the starting point (e.g. a
        // sourceTemplateId passed with startingPoint "default" or "copyEntity").
        .refine(
            (data) =>
                data.sourceTemplateId === undefined ||
                data.startingPoint ===
                    ChartOfAccountsTemplateStartingPoint.copyTemplate,
            {
                message:
                    "sourceTemplateId may only be set when startingPoint is copyTemplate",
                path: ["sourceTemplateId"],
            },
        )
        .refine(
            (data) =>
                data.sourceEntityId === undefined ||
                data.startingPoint ===
                    ChartOfAccountsTemplateStartingPoint.copyEntity,
            {
                message:
                    "sourceEntityId may only be set when startingPoint is copyEntity",
                path: ["sourceEntityId"],
            },
        )
        .refine(
            (data) =>
                data.startingPoint !==
                    ChartOfAccountsTemplateStartingPoint.importCoa ||
                data.rows !== undefined,
            {
                message: "rows are required when startingPoint is importCoa",
                path: ["rows"],
            },
        )
        .refine(
            (data) =>
                data.rows === undefined ||
                data.startingPoint ===
                    ChartOfAccountsTemplateStartingPoint.importCoa,
            {
                message: "rows may only be set when startingPoint is importCoa",
                path: ["rows"],
            },
        );

export type ChartOfAccountsTemplateCreate = z.infer<
    typeof ChartOfAccountsTemplateCreateSchema
>;

export const ChartOfAccountsTemplateUpdateSchema = z
    .object({
        ...chartOfAccountsTemplateHeaderShape,
        name: z.string().min(1, { message: "Required" }).max(255).optional(),
        entityLegalType: z.nativeEnum(EntityProfileType).optional(),
        // Optional on update (omit = unchanged); the header default only applies on create.
        basis: basisSchema.optional(),
        industries: industriesSchema.optional(),
    })
    .refine((body) => Object.keys(body).length > 0, {
        message: "At least one field must be provided",
    });

export type ChartOfAccountsTemplateUpdate = z.infer<
    typeof ChartOfAccountsTemplateUpdateSchema
>;

export const ChartOfAccountsTemplateAccountsResponseSchema = z.object({
    accounts: z.array(ChartOfAccountsTemplateAccountSchema),
    groups: z.array(ChartOfAccountsTemplateAccountGroupSchema),
});

export const ChartOfAccountsTemplateListQuerySchema = z.object({
    includeArchived: zQueryBoolean().optional(),
});

export type ChartOfAccountsTemplateListQuery = z.infer<
    typeof ChartOfAccountsTemplateListQuerySchema
>;

export const ChartOfAccountsTemplateListResponseSchema = z.object({
    chartOfAccountsTemplates: z.array(ChartOfAccountsTemplateListItemSchema),
});
